import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GetData, SERVER_URL } from '../utils/api';
import { useCart } from '../context/CartContext';
import './ProductDetail.css';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, loading: cartLoading } = useCart();

  const [product, setProduct] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState(0);

  // Estados para el carrito
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [addToCartMessage, setAddToCartMessage] = useState('');

  useEffect(() => {
    const loadProduct = async () => {
      try {
        setLoading(true);
        setError('');

        const data = await GetData(`/products/${id}`);

        if (!data || !data.product) {
          setError('Producto no encontrado');
          return;
        }

        setProduct(data.product);

        // Cargar categoría
        if (data.product.categoryId) {
          try {
            const catData = await GetData(`/categories/${data.product.categoryId}`);
            if (catData && catData.category) {
              setCategory(catData.category);
            }
          } catch (catError) {
            console.error('Error cargando categoría:', catError);
          }
        }
      } catch (err) {
        console.error('Error cargando producto:', err);
        const errorMessage = err.message || 'Error al cargar el producto';
        setError(errorMessage.includes('404') || errorMessage.includes('no encontrado')
          ? 'Producto no encontrado'
          : 'Error al cargar el producto. Por favor, intenta de nuevo.');
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id]);

  const handleAddToCart = async () => {
    if (!product || product.stock === 0) {
      setAddToCartMessage('Producto agotado');
      setTimeout(() => setAddToCartMessage(''), 3000);
      return;
    }

    if (quantity < 1 || quantity > product.stock) {
      setAddToCartMessage(`La cantidad debe estar entre 1 y ${product.stock}`);
      setTimeout(() => setAddToCartMessage(''), 3000);
      return;
    }

    // Validar talla si es requerida
    if (product.sizes && product.sizes.length > 0 && !selectedSize) {
      setAddToCartMessage('Debes seleccionar una talla');
      setTimeout(() => setAddToCartMessage(''), 3000);
      return;
    }

    // Validar color si es requerido
    if (product.colors && product.colors.length > 0 && !selectedColor) {
      setAddToCartMessage('Debes seleccionar un color');
      setTimeout(() => setAddToCartMessage(''), 3000);
      return;
    }

    const success = addToCart(product, quantity, selectedSize, selectedColor);

    if (success) {
      setAddToCartMessage('¡Producto agregado al carrito!');
      setTimeout(() => setAddToCartMessage(''), 3000);
      // Resetear formulario
      setQuantity(1);
      setSelectedSize(null);
      setSelectedColor(null);
    } else {
      setAddToCartMessage('Error al agregar al carrito');
      setTimeout(() => setAddToCartMessage(''), 3000);
    }
  };

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    if (value >= 1 && value <= (product?.stock || 1)) {
      setQuantity(value);
    }
  };

  const handleSizeSelect = (size) => {
    setSelectedSize(size === selectedSize ? null : size);
  };

  const handleColorSelect = (color) => {
    setSelectedColor(color === selectedColor ? null : color);
  };

  if (loading) {
    return <div className="loading">Cargando producto...</div>;
  }

  if (error || !product) {
    return (
      <div className="container" style={{ padding: '3rem 0' }}>
        <div className="error-message">
          <h2>Producto no encontrado</h2>
          <button className="btn btn-primary" onClick={() => navigate('/products')}>
            Volver a Productos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="product-detail">
      <div className="container">
        <button className="btn-back" onClick={() => navigate('/products')}>
          ← Volver a productos
        </button>

        <div className="product-detail-content">
          <div className="product-detail-image-container">
            <div className="product-detail-image-main">
              {product.images && product.images.length > 0 ? (
                <img
                  src={`${SERVER_URL}${product.images[selectedImage]}`}
                  alt={product.name}
                  className="product-detail-img"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className="product-detail-image-placeholder" style={product.images && product.images.length > 0 ? {display: 'none'} : {}}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z" fill="currentColor"/>
                </svg>
              </div>
            </div>
            {product.images && product.images.length > 1 && (
              <div className="product-detail-image-thumbnails">
                {product.images.map((image, index) => (
                  <div
                    key={index}
                    className={`product-detail-thumbnail ${selectedImage === index ? 'active' : ''}`}
                    onClick={() => setSelectedImage(index)}
                  >
                    <img src={`${SERVER_URL}${image}`} alt={`${product.name} ${index + 1}`} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="product-detail-info">
            <div className="product-detail-header">
              <div className="product-badges">
                {category && (
                  <span className="product-category-badge">{category.name}</span>
                )}
                {product.featured && (
                  <span className="product-featured-badge">Destacado</span>
                )}
              </div>
              {/* <button
                className={`wishlist-btn-detail ${isInWishlist ? 'active' : ''}`}
                onClick={toggleWishlist}
                disabled={wishlistLoading}
                title={isInWishlist ? 'Quitar de favoritos' : 'Agregar a favoritos'}
              >
                {wishlistLoading ? (
                  <span>⏳</span>
                ) : (
                  <span>{isInWishlist ? '❤️' : '🤍'}</span>
                )}
              </button> */}
            </div>

            <h1 className="product-detail-title">{product.name}</h1>
            <div className="product-detail-price-container">
              {product.discount > 0 ? (
                <>
                  <span className="product-detail-price-discounted">${product.finalPrice?.toFixed(2) || (product.price * (1 - product.discount / 100)).toFixed(2)}</span>
                  <span className="product-detail-price-original">${product.price.toFixed(2)}</span>
                  <span className="product-detail-discount-badge">-{product.discount}% Descuento</span>
                </>
              ) : (
                <p className="product-detail-price">${product.price.toFixed(2)}</p>
              )}
            </div>

            <div className="product-detail-description">
              <h3>Descripción</h3>
              <p>{product.description || 'Sin descripción disponible'}</p>
            </div>

            {product.sizes && product.sizes.length > 0 && (
              <div className="product-detail-options">
                <h3>Tamaños disponibles</h3>
                <div className="product-options-list">
                  {product.sizes.map((size, index) => (
                    <button
                      key={index}
                      className={`product-option ${selectedSize === size ? 'selected' : ''}`}
                      onClick={() => handleSizeSelect(size)}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {product.colors && product.colors.length > 0 && (
              <div className="product-detail-options">
                <h3>Colores disponibles</h3>
                <div className="product-options-list">
                  {product.colors.map((color, index) => (
                    <button
                      key={index}
                      className={`product-option ${selectedColor === color ? 'selected' : ''}`}
                      onClick={() => handleColorSelect(color)}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Selector de cantidad y botón de agregar al carrito */}
            <div className="product-detail-cart-section">
              <div className="quantity-selector">
                <label htmlFor="quantity">Cantidad:</label>
                <input
                  type="number"
                  id="quantity"
                  min="1"
                  max={product.stock || 1}
                  value={quantity}
                  onChange={handleQuantityChange}
                  disabled={product.stock === 0}
                />
              </div>

              <button
                className={`add-to-cart-btn ${product.stock === 0 ? 'disabled' : ''}`}
                onClick={handleAddToCart}
                disabled={product.stock === 0 || cartLoading}
              >
                {cartLoading ? 'Agregando...' : 'Agregar al Carrito'}
              </button>
            </div>

            {/* Mensaje de feedback */}
            {addToCartMessage && (
              <div className={`cart-message ${addToCartMessage.includes('Error') || addToCartMessage.includes('debe') ? 'error' : 'success'}`}>
                {addToCartMessage}
              </div>
            )}

            <div className="product-detail-stock">
              <span className={product.stock > 0 ? 'in-stock' : 'out-of-stock'}>
                {product.stock > 0
                  ? `${product.stock} unidades disponibles`
                  : 'Agotado'}
              </span>
            </div>

            {/* <div className="product-info-note">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{width: '18px', height: '18px', marginRight: '8px'}}>
                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V11H13V17ZM13 9H11V7H13V9Z" fill="currentColor"/>
              </svg>
              <p>Este es un catálogo de muestra. No se procesan compras reales.</p>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
